import {
  useState,
  useRef,
  useEffect,
  ReactNode,
  useCallback,
  createContext,
  useMemo,
} from "react"

import { HamburgerIcon } from "@chakra-ui/icons"
import {
  Box,
  VStack,
  Text,
  Center,
  IconButton,
  Avatar,
  useMediaQuery,
  HStack,
  Button,
} from "@chakra-ui/react"
import { Link, useLocation, useNavigate } from "react-router-dom"

import { useAppDispatch, useAppSelector } from "app/hooks"
import house from "assets/images/house.png"
import ActiveDot from "components/active-dot/active-dot"
import { AppRoutes } from "components/constants/appRoutes"
import ChatIcon from "components/icons/chat_icon"
import QuestionWithRoundIcon from "components/icons/questionWithRoundIcon"
import HomeSolidIcon from "components/icons/rbo/home_solid_icon"
import ReviewIcon from "components/icons/rbo/review_icon"
import WhiteWhimstayLogo from "components/icons/rbo/WhiteWhimstayLogo"
import ReportChartIcon from "components/icons/reportChartIcon"
import SettingsIcon from "components/icons/SettingsIcon"
import SignOutIcon from "components/icons/signOutIcon"
import SideMenuAccordion from "components/side-menu-accordion"
import useIsUnreadReviewApi from "hooks/swr/rbo/useIsUnreadReviewApi"
import { mixpanelClient } from "services/mixpanel/mixpanel"
import { logOut, selectCurrentPmc, selectUser } from "store/auth/authSlice"
import { selectIsUnreadReviews, setIsUnreadReviews } from "store/ui/uiSlice"

import { useSendbirdContext } from "../../sendbird/useSendbirdContext"

// Context to provide `isSidebarOpen` value
const SidebarContext = createContext<{ isSidebarOpen: boolean }>({
  isSidebarOpen: false,
})

// host rbo side bar layout file
const HostRboLayout = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [isSidebarOpen, setSidebarOpen] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const user = useAppSelector(selectUser)
  const currentPmc = useAppSelector(selectCurrentPmc)
  const [windowHeight, setWindowHeight] = useState(window.innerHeight)
  // Detect if the screen size is below lg (responsive view)
  const [isBelowLg] = useMediaQuery("(max-width: 991px)")
  const unreadReview = useAppSelector(selectIsUnreadReviews)
  const { unreadCount } = useSendbirdContext()

  const { data } = useIsUnreadReviewApi({
    pmcId: currentPmc?.pmc_id || "",
  })

  useEffect(() => {
    dispatch(setIsUnreadReviews(data?.isWhimstayReviewUnRead || false))
  }, [data, dispatch])

  // Close sidebar if clicked outside (for responsive screens only)
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setSidebarOpen(false) // Close sidebar if clicked outside
      }
    }
    if (isSidebarOpen) {
      document.addEventListener("mousedown", handleOutsideClick)
    }
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick)
    }
  }, [isSidebarOpen, isBelowLg])
  useEffect(() => {
    const handleResize = () => setWindowHeight(window.innerHeight)
    window.addEventListener("resize", handleResize)
    // Cleanup event listener on unmount
    return () => window.removeEventListener("resize", handleResize)
  }, [])
  const onLogout = useCallback(() => {
    dispatch(logOut())
    navigate(AppRoutes.HOME)
  }, [dispatch])

  // ✅ Memoize context value to prevent re-creation on every render
  const sidebarContextValue = useMemo(
    () => ({ isSidebarOpen }),
    [isSidebarOpen],
  )
  return (
    <Box display='flex' height='100vh'>
      {/* Sidebar */}
      <Box
        ref={sidebarRef}
        position='fixed'
        left={0}
        top='0'
        width={{
          base: isSidebarOpen ? "244px" : "0px",
          lg: isSidebarOpen ? "232px" : "116px",
        }}
        height='100%'
        bg='#006F80'
        color='white'
        transition='all 0.3s ease-in-out'
        zIndex='10'
        boxShadow='md'
        display='flex'
        flexDirection='column'
        justifyContent='space-between'
        borderBottomRightRadius='10px'
        borderTopRightRadius='10px'
        overflowX='hidden'
        onClick={() => setSidebarOpen(true)}
      >
        {/* Logo Section */}
        <Box
          px='4'
          pt={{ base: 5, lg: "61px" }}
          pb={windowHeight < 631 ? 0 : 4}
        >
          <Center mb={4}>
            <Link
              to='/'
              onClick={(e) => {
                e.stopPropagation()
                setSidebarOpen(false)
              }}
            >
              <WhiteWhimstayLogo />
            </Link>
          </Center>
          <Box pl={isSidebarOpen ? 4 : 0}>
            <Button
              variant='ghost'
              textAlign='left'
              alignItems='center'
              p={0}
              leftIcon={<HomeSolidIcon width='22px' height='22px' />}
              iconSpacing={0}
              w='full'
              mt={4}
              justifyContent={isSidebarOpen ? "flex-start" : "center"}
              onClick={(event) => {
                if (isSidebarOpen) {
                  event.stopPropagation()
                  navigate(AppRoutes.HOME)
                }
              }}
            >
              {isSidebarOpen ? (
                <Text color='white' fontWeight={500} ml={2} mt='3px' pl={1.5}>
                  Listings
                </Text>
              ) : (
                ""
              )}
            </Button>
          </Box>
          <Box pl={isSidebarOpen ? 4 : 0}>
            <Button
              variant='ghost'
              textAlign='left'
              alignItems='center'
              p={0}
              leftIcon={
                <Box position='relative'>
                  <ChatIcon width='22px' height='22px' />
                  {unreadCount > 0 && <ActiveDot top='-6px' right='-12px' />}
                </Box>
              }
              iconSpacing={0}
              w='full'
              mt={4}
              justifyContent={isSidebarOpen ? "flex-start" : "center"}
              onClick={(event) => {
                if (isSidebarOpen) {
                  event.stopPropagation()
                  navigate(AppRoutes.MESSAGES)
                }
              }}
            >
              {isSidebarOpen ? (
                <Text color='white' fontWeight={500} ml={2} mt='3px' pl={1.5}>
                  Messages
                </Text>
              ) : (
                ""
              )}
            </Button>
          </Box>
          <Box pl={isSidebarOpen ? 4 : 0}>
            <Button
              variant='ghost'
              textAlign='left'
              alignItems='center'
              p={0}
              leftIcon={
                <Box position='relative'>
                  <ReviewIcon width='22px' height='22px' />
                  {unreadReview && <ActiveDot top='-6px' right='-12px' />}
                </Box>
              }
              iconSpacing={0}
              w='full'
              mt={4}
              justifyContent={isSidebarOpen ? "flex-start" : "center"}
              onClick={(event) => {
                if (isSidebarOpen) {
                  event.stopPropagation()
                  navigate(AppRoutes.REVIEWS)
                }
              }}
            >
              {isSidebarOpen ? (
                <Text color='white' fontWeight={500} ml={2} pl={1.5} mb={1.5}>
                  Reviews
                </Text>
              ) : (
                ""
              )}
            </Button>
          </Box>
          <Box pl={isSidebarOpen ? 4 : 0} pt='0.5px'>
            <SideMenuAccordion
              isSidebarOpen={isSidebarOpen}
              menuTitle='Reports'
              menuIcon={<ReportChartIcon />}
              menuItems={[
                {
                  title: "Earnings",
                  path: AppRoutes.EARNING_PROGRESS,
                },
                {
                  title: "Reservations",
                  path: AppRoutes.RESERVATIONS,
                },
                {
                  title: "Payouts",
                  path: AppRoutes.PAYOUT,
                },
              ]}
            />
          </Box>
          <Box pl={isSidebarOpen ? 4 : 0} pt='0.5px'>
            <SideMenuAccordion
              isSidebarOpen={isSidebarOpen}
              menuTitle='Settings'
              menuIcon={<SettingsIcon />}
              menuItems={[
                {
                  title: "Profile details",
                  path: AppRoutes.PROFILE_DETAILS,
                },
                {
                  title: "Payout method",
                  path: AppRoutes.PAYOUT_SETTINGS,
                },
              ]}
            />
          </Box>
        </Box>

        {/* Bottom Icons */}
        <VStack
          spacing='4'
          align={isSidebarOpen ? "start" : "center"}
          px='4'
          pb={4}
        >
          <Box pl={isSidebarOpen ? 4 : 0}>
            <Button
              variant='ghost'
              textAlign='left'
              alignItems='center'
              p={0}
              leftIcon={<QuestionWithRoundIcon fill='white' />}
              iconSpacing={0}
              w='full'
              mt={windowHeight < 615 ? 1.5 : 4}
              justifyContent={isSidebarOpen ? "flex-start" : "center"}
              onClick={(event) => {
                if (isSidebarOpen) {
                  event.stopPropagation()
                  window.open("https://help.whimstay.com", "_blank")
                  // Mixpanel event on Help
                  mixpanelClient.RBO_SUPPORT_CLICKED()
                }
              }}
            >
              {isSidebarOpen ? (
                <Text color='white' fontWeight={500} ml={2} mt='3px' pl={1.5}>
                  Help
                </Text>
              ) : (
                ""
              )}
            </Button>
            <Button
              variant='ghost'
              textAlign='left'
              alignItems='center'
              p={0}
              onClick={() => {
                if (isSidebarOpen) {
                  onLogout()
                  // Mixpanel event on sign out
                  mixpanelClient.RBO_USER_LOGOUT()
                }
              }}
              leftIcon={<SignOutIcon ml={isSidebarOpen ? "-4px" : ""} />}
              iconSpacing={0}
              w='full'
              mt={4}
              justifyContent={isSidebarOpen ? "flex-start" : "center"}
            >
              {isSidebarOpen ? (
                <Text color='white' fontWeight={500} ml={2} mt='5px'>
                  Sign Out
                </Text>
              ) : (
                ""
              )}
            </Button>
          </Box>

          {/* Profile Section */}
          <Box display='flex' alignItems='center'>
            {(user?.profileImageFullPath ||
              user?.hostProfile?.hostProfileImageUrl) && (
              <Avatar
                name='Whimstay Host'
                src={
                  user?.profileImageFullPath ||
                  user?.hostProfile?.hostProfileImageUrl ||
                  house
                }
                width='54px'
                height='54px'
              />
            )}
            {isSidebarOpen ? (
              <Box pl={2}>
                <Text
                  fontSize='12px'
                  color='white'
                  fontWeight={300}
                  noOfLines={1}
                  width='160px'
                  display='block'
                  textOverflow='ellipsis'
                  whiteSpace='nowrap'
                >
                  {user?.emailId}
                </Text>
                <Text
                  fontSize='14px'
                  color='white'
                  fontWeight={500}
                  noOfLines={1}
                >
                  {user?.fullName}
                </Text>
              </Box>
            ) : (
              ""
            )}
          </Box>
        </VStack>
      </Box>

      {/* Hamburger Menu (only for responsive screens) */}
      {isBelowLg && (
        <HStack
          position='fixed'
          py='2'
          px={5}
          zIndex={4}
          height='90px'
          w='100%'
          bg={
            location.pathname === AppRoutes.HOME ||
            location.pathname.endsWith("/review-publish-listing")
              ? "transparent"
              : "white"
          }
        >
          <IconButton
            aria-label='Open Menu'
            icon={<HamburgerIcon />}
            onClick={() => setSidebarOpen(true)}
          />
        </HStack>
      )}

      {/* Main Content */}
      <SidebarContext.Provider value={sidebarContextValue}>
        <Box
          flex='1'
          marginLeft={{ lg: isSidebarOpen ? "232px" : "116px" }} // Move content dynamically
          width={`calc(100% - ${isSidebarOpen ? "232px" : "116px"})`} // Adjust width
          transition='all 0.3s ease'
          mt={{ base: "90px", lg: 0 }}
        >
          {/* Content Area */}
          <Box>{children}</Box>
        </Box>
      </SidebarContext.Provider>
    </Box>
  )
}
export { SidebarContext }
export default HostRboLayout
